import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import Button from '../../components/ui/Button'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import LoadingSkeleton from '../../components/ui/LoadingSkeleton'
import PageHeader from '../../components/ui/PageHeader'
import SectionCard from '../../components/ui/SectionCard'
import { useToast } from '../../components/ui/ToastProvider'
import { getApiErrorCode, getApiErrorStatus } from '../../api/apiError'
import { GRANTS } from '../../auth/grants'
import { useGrants } from '../../auth/useCurrentUser'
import { rowActionLabel } from '../../lib/deleteLabel'
import AgendaFormDialog from './components/AgendaFormDialog'
import ResponsePanel from './components/ResponsePanel'
import ResponsesTable from './components/ResponsesTable'
import AgendaSummaryCard from './components/AgendaSummaryCard'
import SeriesScopeDialog from './components/SeriesScopeDialog'
import { useAgenda, useDeleteAgenda } from './hooks/useAgendas'
import { personName, type AgendaDetail, type AgendaParticipant, type AgendaScope } from './types'
import { useProxyMode } from '../proxy/useProxyMode'
import './agenda.css'
import '../admin/admin.css'

const LIST_PATH = '/minutes/agenda'

/** Ajanda yetkisi olmayan davetlinin dönüş adresi — takvim ona kilitli (15.10.2026). */
const HOME_PATH = '/'

/**
 * Takvime dönüş adresi: çipin `state.from`'u (hafta dahil), yoksa takvim. Yalnız takvim yolu kabul
 * edilir — `state` başka yere yönlendirmek için kullanılamasın. Ajanda yetkisi olmayan kullanıcı
 * (yalnız davetli) takvimi açamaz; onun için dönüş anasayfadır.
 */
function useBackPath(): string {
  const state: unknown = useLocation().state
  const { hasGrant } = useGrants()
  if (!hasGrant(GRANTS.AGENDA_VIEW)) return HOME_PATH
  const from = state && typeof state === 'object' && 'from' in state ? state.from : undefined
  if (typeof from !== 'string') return LIST_PATH
  return from === LIST_PATH || from.startsWith(`${LIST_PATH}?`) ? from : LIST_PATH
}

/** `confirmDialog()` tüm `<ConfirmDialog>` örneklerine yayın yapar; grupsuz çağrı iki pencere açardı. */
const DELETE_GROUP = 'agenda-delete'

/**
 * Davet detayı (`/minutes/agenda/:id`). Düzenleyen yanıt dökümünü, davetli adları ve kendi yanıt
 * panelini görür; dal alanın varlığına bakar, rol tahmin edilmez. 404'te kendi uyarısıyla takvime döner.
 */
export default function AgendaDetailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const id = Number(useParams().id)
  const detail = useAgenda(id)
  const backPath = useBackPath()

  useEffect(() => {
    if (getApiErrorStatus(detail.error) !== 404) return
    toast.warn(t('agenda.notFound'))
    navigate(backPath, { replace: true })
  }, [detail.error, navigate, toast, t, backPath])

  // Geçersiz kimlik: takvime değil dönüş adresine — yetkisiz davetli için o adres anasayfadır.
  if (!(id > 0)) return <Navigate to={backPath} replace />

  const agenda = detail.data
  if (!agenda) {
    return (
      <div className="admin-page agenda-detail">
        <PageHeader title={t('nav.tutanak.agenda')} actions={<BackButton />} />
        {detail.isLoading && <LoadingSkeleton lines={5} />}
      </div>
    )
  }
  return <AgendaView agenda={agenda} onStale={() => detail.refetch()} />
}

/**
 * Geri: tarayıcı geçmişi değil takvim yolu (detay doğrudan açılmış olabilir). Ajanda yetkisi olmayan
 * davetliye çizilmez — "Takvime Dön" onu kilitli bir ekrana götürürdü.
 */
function BackButton() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const backPath = useBackPath()
  const { hasGrant } = useGrants()
  if (!hasGrant(GRANTS.AGENDA_VIEW)) return null
  return (
    <Button
      variant="secondary"
      size="small"
      icon="pi pi-arrow-left"
      label={t('agenda.backToList')}
      onClick={() => navigate(backPath)}
    />
  )
}

interface ViewProps {
  agenda: AgendaDetail
  /** Detayı sunucudan yeniden okur (409 sonrası). */
  onStale: () => void
}

function AgendaView({ agenda, onStale }: Readonly<ViewProps>) {
  const { t } = useTranslation()
  const toast = useToast()
  /**
   * Yazma yüzeyleri iki koşulun İKİSİNİ birden ister: sunucu bu daveti düzenlenebilir sayıyor mu
   * (`agenda.editable` — kullanıcı düzenleyen mi) VE kip yazmaya izin veriyor mu. İkincisi 126 ile
   * geldi: Kısıtlı Yetkili asistan, yöneticinin KENDİ davetini bile düzenleyemez/silemez. Katılım
   * yanıtı bu kuralın dışındadır — aşağıda.
   */
  const { readOnly } = useProxyMode()
  const canWrite = agenda.editable && !readOnly
  const [formOpen, setFormOpen] = useState(false)
  /** Seriye bağlı toplantıda seçilen kapsam (126); tek toplantıda kullanılmaz. */
  const [formScope, setFormScope] = useState<AgendaScope | undefined>(undefined)

  const openForm = (scope?: AgendaScope) => {
    setFormScope(scope)
    setFormOpen(true)
  }

  const onSaved = () => {
    toast.success(t(formScope === 'SERIES' ? 'agenda.recurrence.seriesUpdated' : 'agenda.updated'))
    setFormOpen(false)
  }

  // Bayat damga (409) ya da davet bu arada bitti → form kapanır, detay tazelenir.
  const onConflict = () => {
    setFormOpen(false)
    onStale()
  }

  return (
    <div className="admin-page agenda-detail">
      {/* `group` ZORUNLU — bkz. DELETE_GROUP. */}
      <ConfirmDialog group={DELETE_GROUP} />
      <PageHeader
        title={agenda.title}
        actions={
          <>
            <BackButton />
            {canWrite && <OrganizerActions agenda={agenda} onEdit={openForm} onStale={onStale} />}
          </>
        }
      />

      <AgendaSummaryCard agenda={agenda} />

      {/* Katılım yanıtı `readOnly`'ye BAĞLI DEĞİL (26.10.2026, kullanıcı kararı): Kısıtlı Yetkili
          asistan da yöneticinin katılım durumunu seçebilir. Kısıtlı kipte açık kalan tek yazma
          yüzeyi budur; sunucudaki karşılığı `ProxyValidationFilter`'ın dar muafiyeti. Düzenle/sil
          ve yeni davet yukarıdaki `canWrite` ile kapalı kalır. */}
      {agenda.myParticipation && (
        <ResponsePanel
          agendaId={agenda.id}
          participation={agenda.myParticipation}
          recurring={!!agenda.series}
          participants={agenda.participants}
          ownerEmployeeId={agenda.ownerEmployeeId}
        />
      )}

      {agenda.responses ? (
        <ResponsesTable responses={agenda.responses} summary={agenda.summary} />
      ) : (
        <ParticipantList participants={agenda.participants} />
      )}

      {canWrite && (
        <AgendaFormDialog
          visible={formOpen}
          initial={agenda}
          scope={formScope}
          onHide={() => setFormOpen(false)}
          onSaved={onSaved}
          onConflict={onConflict}
        />
      )}
    </div>
  )
}

/**
 * Düzenleyen eylemleri: Düzenle (birincil) + Sil (onaylı, yıkıcı). Seriye bağlı toplantıda ikisi de önce
 * kapsamı sorar (126, FR-010/FR-015): düzenlemede seçim formun kipini belirler, silmede seçimin kendisi
 * onaydır.
 */
function OrganizerActions({
  agenda,
  onEdit,
  onStale,
}: Readonly<{ agenda: AgendaDetail; onEdit: (scope?: AgendaScope) => void; onStale: () => void }>) {
  const { t } = useTranslation()
  const toast = useToast()
  const navigate = useNavigate()
  const backPath = useBackPath()
  const remove = useDeleteAgenda()
  const [asking, setAsking] = useState<'edit' | 'delete' | null>(null)
  const editLabel = rowActionLabel(t, 'agenda.edit', agenda.title)
  const deleteLabel = rowActionLabel(t, 'agenda.delete', agenda.title)
  const recurring = !!agenda.series

  const onDeleteError = (err: unknown) => {
    if (getApiErrorCode(err) !== 'INVALID_STATE') return // merkezî bildirim
    toast.error(t('agenda.errors.ended'))
    onStale()
  }

  const runDelete = (scope?: AgendaScope) =>
    remove.mutate(
      { id: agenda.id, scope },
      {
        onSuccess: () => {
          toast.success(t(scope === 'SERIES' ? 'agenda.recurrence.seriesDeleted' : 'agenda.deleted'))
          navigate(backPath)
        },
        onError: onDeleteError,
      },
    )

  const confirmDelete = () =>
    confirmDialog({
      group: DELETE_GROUP,
      header: t('agenda.delete'),
      message: t('agenda.deleteConfirm', { title: agenda.title }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('common.delete'),
      rejectLabel: t('common.cancel'),
      accept: () => runDelete(),
    })

  const onScopeChosen = (scope: AgendaScope) => {
    const action = asking
    setAsking(null)
    if (action === 'edit') onEdit(scope)
    else if (action === 'delete') runDelete(scope)
  }

  return (
    <>
      {recurring && (
        <SeriesScopeDialog
          visible={asking !== null}
          header={t(asking === 'delete' ? 'agenda.recurrence.scope.deleteHeader' : 'agenda.recurrence.scope.editHeader')}
          message={t(
            asking === 'delete' ? 'agenda.recurrence.scope.deleteMessage' : 'agenda.recurrence.scope.editMessage',
            { title: agenda.title },
          )}
          destructive={asking === 'delete'}
          onChoose={onScopeChosen}
          onHide={() => setAsking(null)}
        />
      )}
      <Button
        icon="pi pi-pencil"
        label={t('agenda.edit')}
        aria-label={editLabel}
        title={editLabel}
        disabled={remove.isPending}
        onClick={() => (recurring ? setAsking('edit') : onEdit())}
      />
      <Button
        variant="secondary"
        severity="danger"
        icon="pi pi-trash"
        label={t('agenda.delete')}
        aria-label={deleteLabel}
        title={deleteLabel}
        loading={remove.isPending}
        disabled={remove.isPending}
        onClick={() => (recurring ? setAsking('delete') : confirmDelete())}
      />
    </>
  )
}

/**
 * Davetli görünümü: katılımcıların adları; bir davetlinin yerine katılan kişi işaretlenir (131,
 * 2026-11-03) — kimin yerine olduğu davetliye gösterilmez, sunucu da göndermez.
 */
function ParticipantList({ participants }: Readonly<{ participants: AgendaParticipant[] }>) {
  const { t } = useTranslation()
  return (
    <SectionCard
      title={t('agenda.detail.participants')}
      hint={t('agenda.attendeeCount', { count: participants.length })}
    >
      <ul className="agenda-participants">
        {participants.map((p) => (
          <li key={p.employeeId}>
            {personName(p)}
            {p.substituting && <span className="agenda-muted"> · {t('agenda.detail.substituteTag')}</span>}
          </li>
        ))}
      </ul>
    </SectionCard>
  )
}
